import React, {useEffect, useMemo, useState, useCallback} from 'react';
import {connect} from 'react-redux';
import {MapContainer, useMap, GeoJSON} from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import Loader from '../layout/loading/loader';
import CustomTileLayer from '../layout/map/CustomTileLayer';
import FullscreenControl from '../map/FullscreenControl';
import {graffitiActions} from '../../../modules/graffiti/graffiti.actions';
import { Typography } from '@mui/material';
import L from 'leaflet';

function CenterMap({center, zoom}) {
    const map = useMap();
    useEffect(() => {
        if (center && Array.isArray(center) && center.length === 2) {
            map.setView(center, zoom || 13);
        }
    }, [center, zoom, map]);
    return null;
}

// Normalize various bbox shapes into Leaflet LatLngBounds
function normalizeBboxToBounds(bbox) {
    try {
        if (!bbox) return null;
        // Object form {south, west, north, east}
        if (bbox.south !== undefined && bbox.west !== undefined && bbox.north !== undefined && bbox.east !== undefined) {
            return L.latLngBounds([
                [Number(bbox.south), Number(bbox.west)],
                [Number(bbox.north), Number(bbox.east)]
            ]);
        }
        // Array form
        if (Array.isArray(bbox)) {
            // [[south, west], [north, east]]
            if (bbox.length === 2 && Array.isArray(bbox[0]) && Array.isArray(bbox[1])) {
                return L.latLngBounds([
                    [Number(bbox[0][0]), Number(bbox[0][1])],
                    [Number(bbox[1][0]), Number(bbox[1][1])]
                ]);
            }
            // GeoJSON-like [west, south, east, north]
            if (bbox.length === 4 && bbox.every(v => typeof v === 'number' || typeof v === 'string')) {
                const west = Number(bbox[0]);
                const south = Number(bbox[1]);
                const east = Number(bbox[2]);
                const north = Number(bbox[3]);
                return L.latLngBounds([
                    [south, west],
                    [north, east]
                ]);
            }
        }
    } catch (e) {
        // ignore and fallback
    }
    return null;
}

function extractVilleLocation(metadata) {
    const loc = metadata?.ville?.location;
    if (!loc) return null;
    const lat = loc.lat != null ? Number(loc.lat) : undefined;
    const lng = loc.lng != null ? Number(loc.lng) : undefined;
    const bounds = normalizeBboxToBounds(loc.bbox);
    return { lat, lng, bounds };
}

function ViewportFromMetadata({ metadata, fallbackCenter, fallbackZoom = 13 }) {
    const map = useMap();
    useEffect(() => {
        const loc = extractVilleLocation(metadata);
        if (loc?.bounds) {
            map.fitBounds(loc.bounds, { padding: [50, 50] });
            return;
        }
        if (typeof loc?.lat === 'number' && typeof loc?.lng === 'number') {
            map.setView([loc.lat, loc.lng], fallbackZoom);
            return;
        }
        if (Array.isArray(fallbackCenter)) {
            map.setView(fallbackCenter, fallbackZoom);
        }
    }, [metadata, map, fallbackCenter, fallbackZoom]);
    return null;
}

// Move FitAllBounds to top-level to keep hooks order absolutely stable
function FitAllBounds({ features }) {
    const map = useMap();
    useEffect(() => {
        if (!features || features.length === 0) return;
        try {
            const group = L.featureGroup();
            features.forEach(f => {
                if (f?.geometry) {
                    const layer = L.geoJSON(f);
                    group.addLayer(layer);
                }
            });
            const bounds = group.getBounds();
            if (bounds && bounds.isValid()) {
                map.fitBounds(bounds, { padding: [30, 30] });
            }
        } catch (e) {
            // ignore
        }
    }, [features, map]);
    return null;
}

function resolveCenter(metadata) {
    if (!metadata) return null;
    // Flexible detection of center from metadata
    if (metadata.center && typeof metadata.center.lat === 'number' && typeof metadata.center.lng === 'number') {
        return [metadata.center.lat, metadata.center.lng];
    }
    if (typeof metadata.latitude === 'number' && typeof metadata.longitude === 'number') {
        return [metadata.latitude, metadata.longitude];
    }
    if (Array.isArray(metadata.center) && metadata.center.length === 2) {
        return metadata.center;
    }
    // Try to use first commune center if available
    const communes = metadata.communes || [];
    if (communes.length > 0) {
        const c0 = communes[0];
        if (c0 && typeof c0.latitude === 'number' && typeof c0.longitude === 'number') {
            return [c0.latitude, c0.longitude];
        }
        if (c0 && c0.center && typeof c0.center.lat === 'number' && typeof c0.center.lng === 'number') {
            return [c0.center.lat, c0.center.lng];
        }
    }
    return null;
}

function GraffitiMap({dispatch, userClient, graffitiState, selectedCommunes = [], selectedQuartiers = [], selectedRues = []}) {
    const {metadata, communes, quartiers, loading} = graffitiState || {};

    // Resolve clientId from Redux or localStorage fallback to survive reloads
    const clientId = useMemo(() => {
        const fromRedux = userClient?.id;
        if (fromRedux) return fromRedux;
        try {
            const stored = window.localStorage.getItem('graffitiClientSelected');
            const num = stored ? Number(stored) : null;
            return Number.isFinite(num) && num > 0 ? num : null;
        } catch (e) {
            return null;
        }
    }, [userClient]);

    // Load metadata first to center on client city
    useEffect(() => {
        if (clientId) {
            dispatch(graffitiActions.fetchMetadata(clientId));
        }
        return () => {
            // nothing for now
        };
    }, [dispatch, clientId]);

    // If metadata doesn't provide communes/quartiers arrays, load them explicitly
    useEffect(() => {
        if (!clientId) return;
        const md = metadata || {};
        if (!md.communes && !communes) {
            dispatch(graffitiActions.fetchCommunes(clientId));
        }
        if (!md.quartiers && !quartiers) {
            dispatch(graffitiActions.fetchQuartiers(clientId));
        }
    }, [dispatch, clientId, metadata, communes, quartiers]);

    const [colorByKey, setColorByKey] = useState({});
    const getColorForKey = useCallback((key) => {
        if (!key) return '#ff5722';
        if (colorByKey[key]) return colorByKey[key];
        // Generate a high-contrast color: avoid light yellows/greens and favor darker tones.
        // Allowed hue ranges exclude ~45–150° (yellow/green). Use reds/oranges and blues/purples.
        const ranges = [ [0, 40], [200, 360] ];
        const [min, max] = ranges[Math.floor(Math.random() * ranges.length)];
        const hue = Math.floor(min + Math.random() * (max - min));
        const sat = 85;   // stronger saturation for visibility
        const light = 38; // darker lightness for contrast on light basemaps
        const color = `hsl(${hue} ${sat}% ${light}%)`;
        setColorByKey(prev => ({ ...prev, [key]: color }));
        return color;
    }, [colorByKey]);

    const communeFeaturesById = graffitiState?.communeFeaturesById || {};
    const quartierFeaturesById = graffitiState?.quartierFeaturesById || {};
    const rueFeaturesById = graffitiState?.rueFeaturesById || {};

    const getOptionId = useCallback((opt) => (opt?.id ?? opt?.code ?? opt?.value ?? opt), []);

    // Prefer new API ville.location lat/lng for initial center to avoid empty map before fitBounds
    const initialVilleCenter = useMemo(() => {
        const loc = extractVilleLocation(metadata);
        if (typeof loc?.lat === 'number' && typeof loc?.lng === 'number') {
            return [loc.lat, loc.lng];
        }
        return null;
    }, [metadata]);

    const center = useMemo(() => initialVilleCenter || resolveCenter(metadata) || [48.8566, 2.3522], [initialVilleCenter, metadata]);

    // Filters are driven by parent; no internal search or selection state

    // Fetch features for selected items (with cache in redux)
    useEffect(() => {
        if (!clientId) return;
        (selectedQuartiers || []).forEach((q) => {
            const id = getOptionId(q);
            if (id) dispatch(graffitiActions.fetchQuartierFeature(clientId, id));
        });
    }, [dispatch, clientId, selectedQuartiers, getOptionId]);

    useEffect(() => {
        if (!clientId) return;
        (selectedCommunes || []).forEach((c) => {
            const id = getOptionId(c);
            if (id) dispatch(graffitiActions.fetchCommuneFeature(clientId, id));
        });
    }, [dispatch, clientId, selectedCommunes, getOptionId]);

    useEffect(() => {
        if (!clientId) return;
        (selectedRues || []).forEach((r) => {
            const id = getOptionId(r);
            if (id) dispatch(graffitiActions.fetchRueFeature(clientId, id));
        });
    }, [dispatch, clientId, selectedRues, getOptionId]);

    // Build arrays of features to display
    const selectedQuartierFeatures = useMemo(() => {
        return (selectedQuartiers || [])
            .map(q => quartierFeaturesById[getOptionId(q)])
            .filter(Boolean);
    }, [selectedQuartiers, quartierFeaturesById, getOptionId]);

    const selectedCommuneFeatures = useMemo(() => {
        return (selectedCommunes || [])
            .map(c => communeFeaturesById[getOptionId(c)])
            .filter(Boolean);
    }, [selectedCommunes, communeFeaturesById, getOptionId]);

    const selectedRueFeatures = useMemo(() => {
        return (selectedRues || [])
            .map(r => rueFeaturesById[getOptionId(r)])
            .filter(Boolean);
    }, [selectedRues, rueFeaturesById, getOptionId]);

    // Create a shared canvas renderer early to keep hook order stable across renders
    const canvasRenderer = useMemo(() => L.canvas({ padding: 0.5 }), []);

    // If no clientId can be resolved, prompt the user to select a client instead of infinite loading
    if (!clientId) {
        return (
            <div style={{ padding: 16 }}>
                <Typography variant="body1">Veuillez sélectionner un client graffiti pour afficher la carte.</Typography>
            </div>
        );
    }

    if (loading || !metadata) {
        return <Loader/>;
    }

    return (
        <div>
            <div style={{height: '70vh', width: '100%'}}>
                <MapContainer center={center} zoom={13} preferCanvas={true} style={{height: '100%', width: '100%'}}>
                    {/* Use custom tiles if available, else fallback */}
                    <CustomTileLayer/>
                    <FullscreenControl/>
                    <ViewportFromMetadata metadata={metadata} fallbackCenter={center} fallbackZoom={13} />
                    {selectedCommunes.map((c, idx) => {
                        const id = getOptionId(c);
                        const f = communeFeaturesById[id];
                        if (!f) return null;
                        const key = `commune:${id}`;
                        const color = getColorForKey(key);
                        return (
                            <GeoJSON key={`feature-${key}-${idx}`} data={f} renderer={canvasRenderer} style={{ color, weight: 2, fillOpacity: 0.2, smoothFactor: 1.5 }} interactive={false} bubblingMouseEvents={false} />
                        );
                    })}
                    {selectedQuartiers.map((q, idx) => {
                        const id = getOptionId(q);
                        const f = quartierFeaturesById[id];
                        if (!f) return null;
                        const key = `quartier:${id}`;
                        const color = getColorForKey(key);
                        return (
                            <GeoJSON key={`feature-${key}-${idx}`} data={f} renderer={canvasRenderer} style={{ color, weight: 2, fillOpacity: 0.2, smoothFactor: 1.5 }} interactive={false} bubblingMouseEvents={false} />
                        );
                    })}
                    {selectedRues.map((r, idx) => {
                        const id = getOptionId(r);
                        const f = rueFeaturesById[id];
                        if (!f) return null;
                        const key = `rue:${id}`;
                        const color = getColorForKey(key);
                        return (
                            <GeoJSON
                                key={`feature-${key}-${idx}`}
                                data={f}
                                renderer={canvasRenderer}
                                style={{ color, weight: 3, fillOpacity: 0, smoothFactor: 1.5 }}
                                interactive={false}
                                bubblingMouseEvents={false}
                            />
                        );
                    })}
                    <FitAllBounds features={[...selectedCommuneFeatures, ...selectedQuartierFeatures, ...selectedRueFeatures]} />
                </MapContainer>
            </div>
        </div>
    );
}

const mapStateToProps = (state) => ({
    userClient: state.users?.user,
    graffitiState: state.graffiti,
});

export default connect(mapStateToProps)(React.memo(GraffitiMap));
